/**
 * The sidebar, always fully open on desktop (1024+). Smaller screens use the
 * tab bar and its More sheet instead (`TabBar.tsx`).
 * Spec: `brand-redesign-docs/04-SURFACES-AND-COMPONENTS.md` (Sidebar).
 *
 * Light mode wears mint; dark mode wears a teal-black gradient (both in
 * `frame.css`). Destinations are grouped (Workspace, People, Admin) under
 * quiet labels. The active item sits on a pill that slides from item to item
 * on navigation (a sand marker rides its edge in dark mode). Under the brand
 * is the Today card, a miniature hero panel. The foot holds the Light / Dark
 * switch, notifications and the account menu.
 */

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Moon, Search, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { LogoMark } from "@/components/brand/LogoMark";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/lib/tenant";
import { useLessons } from "@/lib/api/lessons";
import { useMessagesUnread } from "@/lib/api/messages";
import { openCommandPalette, paletteShortcutLabel } from "@/lib/commandPalette";
import { formatLongDate, formatTime, localDayRange, todayKey } from "@/lib/format/time";
import { useTheme } from "@/lib/theme";
import { ROUTES } from "@/types/routes";

import { AccountMenu } from "./AccountMenu";
import { GROUP_LABEL, isActivePath, labelFor, visibleNav, type NavDestination } from "./nav";
import { NotificationsButton } from "./Notifications";

export function Sidebar() {
  const { displayName } = useTenant();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const { resolved, setMode } = useTheme();

  const items = visibleNav(user?.role, useTenant().features);
  const unreadMessages = useMessagesUnread(!!user).data ?? 0;
  const groups = (["work", "people", "admin"] as const)
    .map((g) => ({ g, items: items.filter((i) => i.group === g) }))
    .filter((x) => x.items.length > 0);

  return (
    <>
      <div className="fr-side__head">
        <NavLink to={ROUTES.DASHBOARD} className="fr-brand" aria-label={`${displayName}, dashboard`}>
          <LogoMark size={32} variant="gradient" />
          <span className="fr-brand__word">
            <span className="fr-brand__name">{displayName}</span>
            <span className="fr-brand__sub">Workspace</span>
          </span>
        </NavLink>
      </div>

      <TodayCard />

      <button
        type="button"
        className="fr-item fr-search"
        onClick={openCommandPalette}
        aria-keyshortcuts="Control+K Meta+K"
      >
        <span className="fr-item__icon">
          <Search size={18} strokeWidth={1.6} />
        </span>
        <span className="fr-item__label">Search</span>
        <span className="fr-item__trail" aria-hidden>
          <kbd className="fr-kbd">{paletteShortcutLabel()}</kbd>
        </span>
      </button>

      <nav className="fr-nav" aria-label="Main">
        {groups.map(({ g, items: list }, gi) => (
          <div key={g} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {gi > 0 ? (
              <p className="st-label fr-group-label" aria-hidden>
                {GROUP_LABEL[g]}
              </p>
            ) : null}
            {list.map((item) => (
              <SideLink
                key={item.to}
                item={item}
                label={labelFor(item, user?.role)}
                active={isActivePath(pathname, item.to)}
                animate={!reduce}
                count={item.to === ROUTES.MESSAGES ? unreadMessages : 0}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="fr-rule" aria-hidden />

      <ThemeSwitch resolved={resolved} onPick={setMode} animate={!reduce} />

      <NotificationsButton variant="side" />

      <AccountMenu variant="side" />
    </>
  );
}

/** Light / Dark as a two-segment pill; the thumb slides to the pick. */
export function ThemeSwitch({
  resolved,
  onPick,
  animate,
}: {
  resolved: "light" | "dark";
  onPick: (mode: "light" | "dark") => void;
  animate: boolean;
}) {
  const options = [
    { value: "light" as const, label: "Light", Icon: Sun },
    { value: "dark" as const, label: "Dark", Icon: Moon },
  ];
  return (
    <div className="fr-theme" role="radiogroup" aria-label="Theme">
      {options.map(({ value, label, Icon }) => {
        const on = resolved === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={on}
            className="fr-theme__opt"
            onClick={() => onPick(value)}
          >
            {on ? (
              <motion.span
                layoutId={animate ? "theme-thumb" : undefined}
                className="fr-theme__thumb"
                transition={{ type: "spring", stiffness: 520, damping: 40 }}
                aria-hidden
              />
            ) : null}
            <Icon size={14} strokeWidth={1.75} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SideLink({
  item,
  label,
  active,
  animate,
  count = 0,
}: {
  item: NavDestination;
  label: string;
  active: boolean;
  animate: boolean;
  /** Unread items, shown as a count at the end of the row. */
  count?: number;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === ROUTES.DASHBOARD}
      className="fr-item"
      aria-label={count > 0 ? `${label}, ${count} unread` : undefined}
    >
      {active ? (
        <motion.span
          layoutId={animate ? "side-marker" : undefined}
          className="fr-marker"
          transition={{ type: "spring", stiffness: 520, damping: 40 }}
          aria-hidden
        />
      ) : null}
      <span className="fr-item__icon">
        <Icon size={18} strokeWidth={1.6} />
      </span>
      <span className="fr-item__label">{label}</span>
      {count > 0 ? (
        <span className="fr-item__trail" aria-hidden>
          <span className="fr-count">{count > 99 ? "99+" : count}</span>
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * A live card under the brand: the time (ticking every 30 seconds), today's
 * date, and how many lessons are on today, linking to Lessons. Lessons are
 * already scoped by role on the backend, so the count is always "yours".
 */
function TodayCard() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const key = todayKey();
  // Same parameters as the dashboard's day query, so both share one cached
  // request. Only the total is read here.
  const lessons = useLessons({ ...localDayRange(key), page_size: 100, ordering: "scheduled_at" });
  const count = lessons.data?.count;

  return (
    <Link to={ROUTES.LESSONS} className="fr-today" aria-label={`Today, ${formatLongDate(key)}. Open lessons`}>
      <span className="fr-today__top">
        <span className="fr-today__clock">{formatTime(now)}</span>
        <span className="fr-today__live">
          <span className="fr-today__pulse" aria-hidden />
          Today
        </span>
      </span>
      <span className="fr-today__date" style={{ display: "block" }}>
        {formatLongDate(key)}
      </span>
      <span className="fr-today__meta">
        <span>
          {lessons.isError ? (
            "Timetable unavailable"
          ) : count === undefined ? (
            "Loading timetable"
          ) : count === 0 ? (
            "No lessons today"
          ) : (
            <>
              <strong>{count}</strong> {count === 1 ? "lesson" : "lessons"} today
            </>
          )}
        </span>
        <ArrowRight size={13} strokeWidth={1.75} aria-hidden />
      </span>
    </Link>
  );
}
